import { __decorate } from "tslib";
import React from 'react';
import PropTypes from 'prop-types';
import { action as mobxAction, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import isNil from 'lodash/isNil';
import { getConfig, getProPrefixCls } from 'choerodon-ui/lib/configure';
import { Size } from 'choerodon-ui/lib/_util/enum';
import Trigger from 'choerodon-ui/lib/trigger/Trigger';
import { Action } from 'choerodon-ui/lib/trigger/enum';
import Button from '../button/Button';
import { $l } from '../locale-context';
import { ButtonColor, FuncType } from '../button/enum';
import AttachmentList from './AttachmentList';
import { FormField } from '../field/FormField';
import axios from '../axios';
import autobind from '../_util/autobind';
import Modal from '../modal';
import AttachmentFile from '../data-set/AttachmentFile';
import { appendFormData, formatFileSize, sortAttachments } from './utils';
import ObserverSelect from '../select/Select';
import BUILT_IN_PLACEMENTS from '../trigger-field/placements';
import attachmentStore from '../stores/AttachmentStore';
import { DataSetEvents, FieldType } from '../data-set/enum';
import { open } from '../modal-container/ModalContainer';
import Icon from '../icon';
import Tooltip from '../tooltip';
import { ShowHelp } from '../field/enum';
import { FIELD_SUFFIX } from '../form/utils';
import { showValidationMessage } from '../field/utils';
import { ShowValidation } from '../form/enum';
const defaultSort = {
    type: 'time',
    order: 'asc',
    custom: true,
};
let Attachment = class Attachment extends FormField {
    get help() {
        return this.getProp('help');
    }
    get bucketName() {
        return this.getProp('bucketName');
    }
    get bucketDirectory() {
        return this.getProp('bucketDirectory');
    }
    get storageCode() {
        return this.getProp('storageCode');
    }
    get attachments() {
        const { field } = this;
        if (field) {
            return field.attachments;
        }
        return this.observableProps.attachments;
    }
    set attachments(attachments) {
        runInAction(() => {
            const { field } = this;
            if (field) {
                field.attachments = attachments;
            }
            else {
                this.observableProps.attachments = attachments;
            }
            if (attachments) {
                const { onAttachmentsChange } = this.props;
                if (onAttachmentsChange) {
                    onAttachmentsChange(attachments);
                }
            }
        });
    }
    get count() {
        const { attachments, field } = this;
        if (attachments) {
            return attachments.length;
        }
        if (field) {
            const { attachmentCount } = field;
            if (attachmentCount !== undefined) {
                return attachmentCount;
            }
        }
        const { count } = this.observableProps;
        return count;
    }
    get axios() {
        return getConfig('axios') || axios;
    }
    get defaultValidationMessages() {
        const label = this.getProp('label');
        return {
            valueMissing: $l('Attachment', label ? 'value_missing' : 'value_missing_no_label', { label }),
        };
    }
    processDataSetEventListener(on) {
        const { dataSet } = this;
        if (dataSet) {
            dataSet[on ? 'addEventListener' : 'removeEventListener'](DataSetEvents.load, this.handleDataSetLoad);
        }
    }
    componentDidMount() {
        super.componentDidMount();
        this.fetchCount();
        this.processDataSetEventListener(true);
    }
    componentDidUpdate(prevProps) {
        const { value, viewMode } = this.props;
        if (prevProps.value !== value || prevProps.viewMode !== viewMode) {
            this.fetchCount();
        }
    }
    componentWillUnmount() {
        super.componentWillUnmount();
        this.processDataSetEventListener(false);
    }
    getFieldType() {
        return FieldType.attachment;
    }
    getObservableProps(props, context) {
        const { count, attachments } = props;
        const { observableProps = { count, attachments } } = this;
        return {
            ...super.getObservableProps(props, context),
            count: count === undefined ? observableProps.count : count,
            attachments: attachments ? attachments.map(attachment => {
                if (attachment instanceof AttachmentFile) {
                    return attachment;
                }
                return new AttachmentFile(attachment);
            }) : observableProps.attachments,
        };
    }
    getValidAttachments() {
        const { attachments } = this;
        if (attachments) {
            return attachments.filter(({ status }) => !status || ['success', 'done'].includes(status));
        }
    }
    getValidatorProps() {
        const attachments = this.getValidAttachments();
        const count = attachments ? attachments.length : this.count;
        return {
            ...super.getValidatorProps(),
            attachmentCount: count || 0,
        };
    }
    fetchCount() {
        const { field } = this;
        const { viewMode } = this.props;
        if (viewMode !== 'list' && isNil(this.count)) {
            const value = this.getValue();
            if (value) {
                if (field) {
                    field.fetchAttachmentCount(value);
                }
                else {
                    const { batchFetchCount } = getConfig('attachment');
                    if (batchFetchCount && !this.attachments) {
                        attachmentStore.fetchCountInBatch(value).then(mobxAction((count) => {
                            this.observableProps.count = count || 0;
                        }));
                    }
                }
            }
        }
    }
    handleDataSetLoad() {
        this.fetchCount();
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'value',
            'accept',
            'action',
            'data',
            'headers',
            'withCredentials',
            'sortable',
            'listType',
            'viewMode',
            'fileKey',
            'fileSize',
            'bucketName',
            'bucketDirectory',
            'storageCode',
            'count',
            'max',
            'listLimit',
            'showHistory',
            'downloadAll',
            'attachments',
            'onAttachmentsChange',
            'beforeUpload',
            'onUploadProgress',
            'onUploadSuccess',
            'onUploadError',
        ]);
    }
    getUploadAxiosConfig(attachment, attachmentUUID) {
        const { originFileObj } = attachment;
        if (originFileObj) {
            const globalConfig = getConfig('attachment');
            const { action, data, headers, fileKey = globalConfig.defaultFileKey, withCredentials } = this.props;
            const newHeaders = {
                'X-Requested-With': 'XMLHttpRequest',
                ...headers,
            };
            const formData = new FormData();
            formData.append(fileKey, originFileObj);
            if (data) {
                appendFormData(formData, data);
            }
            const onUploadProgress = (e) => {
                this.handleProgress(e.total > 0 ? (e.loaded / e.total) * 100 : 0, attachment);
            };
            if (action) {
                return {
                    url: action,
                    headers: {
                        ...newHeaders,
                    },
                    data: formData,
                    onUploadProgress,
                    method: 'POST',
                    withCredentials,
                };
            }
            const actionHook = globalConfig.action;
            if (actionHook) {
                const { bucketName, bucketDirectory, storageCode } = this;
                const newConfig = typeof actionHook === 'function' ? actionHook({
                    attachment,
                    bucketName,
                    bucketDirectory,
                    storageCode,
                    attachmentUUID,
                }) : actionHook;
                const { data: customData, onUploadProgress: customUploadProgress } = newConfig;
                if (customData) {
                    appendFormData(formData, customData);
                }
                return {
                    withCredentials,
                    method: 'POST',
                    ...newConfig,
                    headers: {
                        ...newHeaders,
                        ...newConfig.headers,
                    },
                    data: formData,
                    onUploadProgress: (e) => {
                        onUploadProgress(e);
                        if (customUploadProgress) {
                            customUploadProgress(e);
                        }
                    },
                };
            }
            throw new Error(`Please set Upload.action or configure.attachment.action .`);
        }
    }
    isAcceptFile(attachment, accept) {
        const acceptTypes = accept.map(type => (new RegExp(type.replace(/\./g, '\\.').replace(/\*/g, '.*'))));
        const { name, type } = attachment;
        return acceptTypes.some(acceptType => acceptType.test(name) || acceptType.test(type));
    }
    getAttachmentUUID() {
        const { getAttachmentUUID } = getConfig('attachment');
        if (!getAttachmentUUID) {
            throw new Error('no getAttachmentUUID hook in global configure.');
        }
        return getAttachmentUUID();
    }
    async uploadAttachments(attachments) {
        const oldAttachmentUUID = this.getValue();
        const attachmentUUID = oldAttachmentUUID || (await this.getAttachmentUUID());
        if (attachmentUUID !== oldAttachmentUUID) {
            this.tempAttachmentUUID = attachmentUUID;
        }
        const max = this.getProp('max');
        if (max > 0 && (this.count || 0) + attachments.length > max) {
            Modal.error($l('Attachment', 'file_list_max_length', { count: max }));
            return;
        }
        const oldAttachments = this.attachments || [];
        if (this.multiple) {
            this.attachments = [...oldAttachments.slice(), ...attachments];
        }
        else {
            oldAttachments.forEach(attachment => this.doRemove(attachment));
            this.attachments = [...attachments];
        }
        if (attachmentUUID) {
            try {
                await Promise.all(attachments.map((attachment) => this.upload(attachment, attachmentUUID)));
            }
            finally {
                this.changeOrder();
                if (this.tempAttachmentUUID) {
                    delete this.tempAttachmentUUID;
                    this.setValue(attachmentUUID);
                }
            }
        }
    }
    async uploadAttachment(attachment, attachmentUUID) {
        await this.upload(attachment, attachmentUUID);
        this.changeOrder();
    }
    async upload(attachment, attachmentUUID) {
        try {
            const result = await this.beforeUpload(attachment, this.attachments);
            if (result === false) {
                this.removeAttachment(attachment);
            }
            else if (attachment.status !== 'error' && !attachment.invalid) {
                const config = this.getUploadAxiosConfig(attachment, attachmentUUID);
                if (config) {
                    await this.handleSuccess(await this.axios(config), attachment);
                }
            }
        }
        catch (e) {
            this.handleError(e, attachment);
            throw e;
        }
    }
    getOtherProps() {
        const otherProps = super.getOtherProps();
        otherProps.onClick = this.handleClick;
        return otherProps;
    }
    handleProgress(percent, attachment) {
        attachment.status = 'uploading';
        attachment.percent = percent;
        const { onUploadProgress } = this.props;
        if (onUploadProgress) {
            onUploadProgress(percent, attachment);
        }
    }
    handleSuccess(response, attachment) {
        attachment.percent = 100;
        return new Promise((resolve) => {
            setTimeout(mobxAction(() => {
                attachment.status = 'success';
                const { onUploadSuccess: handleUploadSuccess } = getConfig('attachment');
                if (handleUploadSuccess) {
                    handleUploadSuccess(response, attachment);
                }
                const { onUploadSuccess } = this.props;
                if (onUploadSuccess) {
                    onUploadSuccess(response, attachment);
                }
                this.checkValidity();
                resolve();
            }), 0);
        });
    }
    handleError(error, attachment) {
        const { onUploadError: handleUploadError } = getConfig('attachment');
        const { response } = error;
        const { onUploadError } = this.props;
        attachment.status = 'error';
        attachment.error = error;
        const { message } = error;
        if (handleUploadError) {
            handleUploadError(error, attachment);
        }
        attachment.errorMessage = attachment.errorMessage || message;
        if (onUploadError) {
            onUploadError(error, response, attachment);
        }
    }
    handleChange(e) {
        const { target } = e;
        if (target.value) {
            const files = [...target.files];
            target.value = '';
            this.autoCreate();
            this.uploadAttachments(files.map((file, index) => new AttachmentFile({
                uid: this.getUid(index),
                url: URL.createObjectURL(file),
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified,
                originFileObj: file,
                creationDate: new Date(),
            })));
        }
    }
    doRemove(attachment) {
        const { onRemove } = getConfig('attachment');
        const attachmentUUID = this.getValue();
        if (onRemove && attachmentUUID) {
            if (attachment.status === 'error') {
                return this.removeAttachment(attachment);
            }
            const { bucketName, bucketDirectory, storageCode } = this;
            attachment.status = 'deleting';
            return onRemove({ attachment, attachmentUUID, bucketName, bucketDirectory, storageCode })
                .then(mobxAction((result) => {
                if (result !== false) {
                    this.removeAttachment(attachment);
                }
                attachment.status = 'done';
            }))
                .catch(mobxAction(() => {
                attachment.status = 'done';
            }));
        }
    }
    handleHistory(attachment, attachmentUUID) {
        const { renderHistory } = getConfig('attachment');
        if (renderHistory) {
            const { bucketName, bucketDirectory, storageCode } = this;
            open({
                title: $l('Attachment', 'operation_records'),
                children: renderHistory({
                    attachment,
                    attachmentUUID,
                    bucketName,
                    bucketDirectory,
                    storageCode,
                }),
                cancelButton: false,
                okText: $l('Modal', 'close'),
                drawer: true,
            });
        }
    }
    handleRemove(attachment) {
        return this.doRemove(attachment);
    }
    handleFetchAttachment(fetchProps) {
        const { field } = this;
        if (field) {
            field.fetchAttachments(fetchProps);
        }
        else {
            const { fetchList } = getConfig('attachment');
            if (fetchList) {
                fetchList(fetchProps).then((results) => {
                    this.attachments = results.map(file => new AttachmentFile(file));
                });
            }
        }
    }
    handlePreview() {
        this.setPopup(false);
    }
    removeAttachment(attachment) {
        const { attachments } = this;
        if (attachments) {
            const index = attachments.indexOf(attachment);
            if (index !== -1) {
                attachments.splice(index, 1);
                this.checkValidity();
                const { onAttachmentsChange } = this.props;
                if (onAttachmentsChange) {
                    onAttachmentsChange(attachments);
                }
            }
        }
        return undefined;
    }
    beforeUpload(attachment, attachments) {
        const { beforeUpload, fileSize = getConfig('attachment').defaultFileSize, accept } = this.props;
        if (accept && !this.isAcceptFile(attachment, accept)) {
            attachment.status = 'error';
            attachment.invalid = true;
            attachment.errorMessage = $l('Attachment', 'file_type_mismatch', { types: accept.join(',') });
            return;
        }
        if (fileSize && fileSize > 0 && attachment.size > fileSize) {
            attachment.status = 'error';
            attachment.invalid = true;
            attachment.errorMessage = $l('Attachment', 'file_max_size', { size: formatFileSize(fileSize) });
            return;
        }
        if (!beforeUpload) {
            return true;
        }
        return beforeUpload(attachment, attachments);
    }
    handleClick(e) {
        const { element } = this;
        if (element) {
            element.click();
        }
        const { onClick } = this.props;
        if (onClick) {
            onClick(e);
        }
    }
    getUid(index) {
        const { prefixCls } = this;
        return `${prefixCls}-${Date.now()}-${index}`;
    }
    renderHeaderLabel() {
        const { viewMode } = this.props;
        if (this.hasFloatLabel || viewMode === 'popup') {
            const label = this.getLabel();
            if (label) {
                return (React.createElement("span", { className: `${this.prefixCls}-header-label` }, label));
            }
        }
    }
    isDisabled() {
        if (super.isDisabled()) {
            return true;
        }
        const max = this.getProp('max');
        if (max) {
            const { count = 0 } = this;
            return count >= max;
        }
        return false;
    }
    renderUploadBtn(isCardButton, label) {
        const { count = 0, multiple, prefixCls, props: { viewMode, }, } = this;
        const buttonProps = this.getOtherProps();
        const { children, ref, className, style, accept, name, fileKey, ...rest } = buttonProps;
        const max = this.getProp('max');
        const uploadProps = {
            multiple,
            accept: accept ? accept.join(',') : undefined,
            name: name || fileKey || getConfig('attachment').defaultFileKey,
            type: 'file',
            ref,
        };
        const width = isCardButton ? this.getPictureWidth() : undefined;
        return isCardButton ? (React.createElement(Button, Object.assign({ funcType: FuncType.link, key: "upload-btn", icon: "add" }, rest, { className: classNames(`${prefixCls}-card-button`, className), style: { ...style, width, height: width } }),
            React.createElement("div", null, children || $l('Attachment', 'upload_picture')),
            max ? React.createElement("div", null, `${count}/${max}`) : count || undefined,
            React.createElement("input", Object.assign({ key: "upload" }, uploadProps, { hidden: true })))) : (React.createElement(Button, Object.assign({ funcType: viewMode === 'popup' ? FuncType.flat : FuncType.link, key: "upload-btn", icon: "file_upload", color: this.isValid ? ButtonColor.primary : ButtonColor.red }, rest, { onMouseEnter: this.handleMouseEnter, onMouseLeave: this.handleMouseLeave }),
            children || $l('Attachment', 'upload_attachment'),
            label && React.createElement(React.Fragment, null,
                "(",
                label,
                ")"),
            " ",
            multiple && (max ? `${count}/${max}` : count) || undefined,
            React.createElement("input", Object.assign({ key: "upload" }, uploadProps, { hidden: true }))));
    }
    showTooltip(e) {
        if (this.showValidation === ShowValidation.tooltip) {
            const message = this.getTooltipValidationMessage();
            if (message) {
                showValidationMessage(e, message);
                return true;
            }
        }
        return false;
    }
    renderViewButton(label) {
        const { multiple, viewMode } = this.props;
        const { children, ...rest } = this.getOtherProps();
        return (React.createElement(Button, Object.assign({ funcType: viewMode === 'popup' ? FuncType.flat : FuncType.link, key: "view-btn", icon: "attach_file", color: ButtonColor.primary }, rest),
            children || $l('Attachment', 'view_attachment'),
            label && React.createElement(React.Fragment, null,
                "(",
                label,
                ")"),
            " ",
            multiple && this.count || undefined));
    }
    handleSort(sort) {
        this.sort = sort;
    }
    handleOrderChange(props) {
        const { attachments } = props;
        this.attachments = attachments;
        this.changeOrder();
    }
    changeOrder() {
        this.sort = {
            ...defaultSort,
            ...this.sort,
            custom: true,
        };
        const attachmentUUID = this.getValue();
        if (attachmentUUID) {
            const attachments = this.getValidAttachments();
            const { onOrderChange } = getConfig('attachment');
            if (onOrderChange && attachments) {
                const { bucketName, bucketDirectory, storageCode } = this;
                onOrderChange({
                    bucketName,
                    bucketDirectory,
                    storageCode,
                    attachments,
                    attachmentUUID,
                });
            }
        }
    }
    getSortSelectPopupContainer() {
        return this.wrapper;
    }
    renderSorter() {
        const { sortable, viewMode } = this.props;
        if (sortable) {
            const { prefixCls, attachments } = this;
            if (attachments && attachments.length > 1) {
                const { type, order } = this.sort || defaultSort;
                return (React.createElement(React.Fragment, null,
                    React.createElement(ObserverSelect, { value: type, onChange: (newType) => this.handleSort({ type: newType, order, custom: false }), clearButton: false, isFlat: true, popupPlacement: "bottomRight", getPopupContainer: viewMode === 'popup' ? this.getSortSelectPopupContainer : undefined, size: Size.small, border: false },
                        React.createElement(ObserverSelect.Option, { value: "time" }, $l('Attachment', 'by_upload_time')),
                        React.createElement(ObserverSelect.Option, { value: "name" }, $l('Attachment', 'by_name'))),
                    React.createElement(Button, { funcType: FuncType.link, className: classNames(`${prefixCls}-order-icon`, order), onClick: () => this.handleSort({ type, order: order === 'asc' ? 'desc' : 'asc', custom: false }) })));
            }
        }
    }
    renderUploadList(uploadButton) {
        const { listType, sortable, listLimit, showHistory, } = this.props;
        const { attachments } = this;
        const attachmentUUID = this.tempAttachmentUUID || this.getValue();
        if (attachmentUUID || uploadButton) {
            const { bucketName, bucketDirectory, storageCode } = this;
            const width = this.getPictureWidth();
            const { readOnly } = this;
            return (React.createElement(AttachmentList, { prefixCls: `${this.prefixCls}-list`, pictureWidth: width, listType: listType, attachments: sortAttachments(attachments, this.sort || defaultSort), bucketName: bucketName, bucketDirectory: bucketDirectory, storageCode: storageCode, attachmentUUID: attachmentUUID, uploadButton: uploadButton, sortable: sortable, readOnly: readOnly, limit: readOnly ? listLimit : undefined, onHistory: showHistory ? this.handleHistory : undefined, onRemove: this.handleRemove, onUpload: this.uploadAttachment, onOrderChange: this.handleOrderChange, onFetchAttachments: this.handleFetchAttachment, onPreview: this.handlePreview }));
        }
    }
    renderHeader(uploadBtn) {
        const { prefixCls, props: { downloadAll, viewMode } } = this;
        const label = this.renderHeaderLabel();
        const buttons = [];
        if (uploadBtn) {
            buttons.push(uploadBtn);
        }
        if (this.readOnly) {
            if (this.count) {
                if (downloadAll) {
                    const { getDownloadAllUrl } = getConfig('attachment');
                    if (getDownloadAllUrl) {
                        const { bucketName, bucketDirectory, storageCode } = this;
                        const attachmentUUID = this.getValue();
                        getDownloadAllUrl({ bucketName, bucketDirectory, storageCode, attachmentUUID });
                        const downProps = {
                            key: 'download',
                            icon: 'get_app',
                            funcType: FuncType.link,
                            href: getDownloadAllUrl({ bucketName, bucketDirectory, storageCode, attachmentUUID }),
                            target: '_blank',
                            color: ButtonColor.primary,
                            children: $l('Attachment', 'download_all'),
                            ...downloadAll,
                        };
                        buttons.push(React.createElement(Button, Object.assign({}, downProps)));
                    }
                }
            }
            else if (viewMode !== 'popup') {
                const viewProps = {
                    key: 'view',
                    funcType: FuncType.link,
                    disabled: true,
                    children: $l('Attachment', 'no_attachments'),
                };
                buttons.push(React.createElement(Button, Object.assign({}, viewProps)));
            }
        }
        return (React.createElement("div", { className: `${prefixCls}-header` },
            label,
            label && buttons.length ? React.createElement("span", { key: "divider", className: `${prefixCls}-header-divider` }) : undefined,
            React.createElement("div", { className: `${prefixCls}-header-buttons` }, buttons),
            this.renderSorter()));
    }
    renderWrapper() {
        const { prefixCls } = this;
        return (React.createElement("div", { className: `${prefixCls}-popup-inner`, ref: this.wrapperReference }, this.renderWrapperList()));
    }
    getTooltipValidationMessage() {
        const validationMessage = this.renderValidationResult();
        if (!this.isValidationMessageHidden(validationMessage)) {
            return validationMessage;
        }
    }
    renderValidationResult(validationResult) {
        const message = super.renderValidationResult(validationResult);
        if (message) {
            return (React.createElement("div", { className: `${this.prefixCls}-validation-message` }, message));
        }
    }
    renderEmpty() {
        const { viewMode } = this.props;
        if (viewMode === 'popup' && !this.count) {
            return (React.createElement("div", { className: `${this.prefixCls}-empty` }, getConfig('renderEmpty')('Attachment')));
        }
    }
    renderWrapperList(uploadBtn) {
        const { prefixCls, props: { viewMode, listType } } = this;
        const isCard = listType === 'picture-card';
        return (React.createElement("div", { className: `${prefixCls}-wrapper` },
            this.renderHeader(!isCard && uploadBtn),
            viewMode !== 'popup' && this.renderHelp(ShowHelp.newLine),
            this.showValidation === ShowValidation.newLine && this.renderValidationResult(),
            this.renderEmpty(),
            viewMode !== 'none' && this.renderUploadList(isCard && uploadBtn)));
    }
    getPictureWidth() {
        const { pictureWidth, listType } = this.props;
        return pictureWidth || (listType === 'picture-card' ? 100 : 48);
    }
    renderHelp(forceHelpMode) {
        const { showHelp } = this.props;
        const help = this.getProp('help');
        if (help === undefined || showHelp === ShowHelp.none)
            return;
        switch (forceHelpMode) {
            case ShowHelp.tooltip:
                return (React.createElement(Tooltip, { title: help, openClassName: `${getConfig('proPrefixCls')}-tooltip-popup-help`, placement: "bottom" },
                    React.createElement(Icon, { type: "help", style: { fontSize: '14px', color: '#8c8c8c' } })));
            default:
                return (React.createElement("div", { key: "help", className: `${getProPrefixCls(FIELD_SUFFIX)}-help` }, help));
        }
    }
    get showValidation() {
        const { viewMode, showValidation = viewMode === 'popup' ? ShowValidation.tooltip : ShowValidation.newLine } = this.props;
        const { context: { showValidation: ctxShowValidation = showValidation } } = this;
        return ctxShowValidation;
    }
    handlePopupHiddenChange(hidden) {
        this.setPopup(!hidden);
    }
    setPopup(popup) {
        this.popup = popup;
    }
    render() {
        const { viewMode, listType, _inTable, hidden } = this.props;
        const { readOnly, prefixCls } = this;
        if (viewMode === 'popup') {
            const label = !_inTable && this.hasFloatLabel && this.getLabel();
            return (React.createElement("div", { className: `${prefixCls}-popup-wrapper` },
                React.createElement(Trigger, { prefixCls: this.prefixCls, popupContent: this.renderWrapper, action: [Action.hover, Action.focus], builtinPlacements: BUILT_IN_PLACEMENTS, popupPlacement: "bottomLeft", popupHidden: hidden || !this.popup, onPopupHiddenChange: this.handlePopupHiddenChange }, readOnly ? this.renderViewButton(label) : this.renderUploadBtn(false, label)),
                this.renderHelp(ShowHelp.tooltip),
                this.showValidation === ShowValidation.newLine && this.renderValidationResult()));
        }
        return this.renderWrapperList(readOnly ? undefined : this.renderUploadBtn(listType === 'picture-card'));
    }
};
Attachment.displayName = 'Attachment';
Attachment.defaultProps = {
    ...FormField.defaultProps,
    suffixCls: 'attachment',
    multiple: true,
    sortable: true,
    downloadAll: true,
    listType: 'text',
    viewMode: 'list',
};
Attachment.propTypes = {
    ...FormField.propTypes,
    sortable: PropTypes.bool,
    listType: PropTypes.oneOf(['text', 'picture', 'picture-card']),
    viewMode: PropTypes.oneOf(['none', 'list', 'popup']),
};
// eslint-disable-next-line camelcase
Attachment.__IS_IN_CELL_EDITOR = true;
__decorate([
    observable
], Attachment.prototype, "sort", void 0);
__decorate([
    observable
], Attachment.prototype, "popup", void 0);
__decorate([
    autobind
], Attachment.prototype, "handleDataSetLoad", null);
__decorate([
    mobxAction
], Attachment.prototype, "uploadAttachments", null);
__decorate([
    autobind
], Attachment.prototype, "uploadAttachment", null);
__decorate([
    mobxAction
], Attachment.prototype, "handleProgress", null);
__decorate([
    mobxAction
], Attachment.prototype, "handleSuccess", null);
__decorate([
    mobxAction
], Attachment.prototype, "handleError", null);
__decorate([
    autobind,
    mobxAction
], Attachment.prototype, "handleChange", null);
__decorate([
    mobxAction
], Attachment.prototype, "doRemove", null);
__decorate([
    autobind
], Attachment.prototype, "handleHistory", null);
__decorate([
    autobind
], Attachment.prototype, "handleRemove", null);
__decorate([
    autobind
], Attachment.prototype, "handleFetchAttachment", null);
__decorate([
    autobind
], Attachment.prototype, "handlePreview", null);
__decorate([
    mobxAction
], Attachment.prototype, "removeAttachment", null);
__decorate([
    mobxAction
], Attachment.prototype, "beforeUpload", null);
__decorate([
    autobind
], Attachment.prototype, "handleClick", null);
__decorate([
    mobxAction
], Attachment.prototype, "handleSort", null);
__decorate([
    autobind,
    mobxAction
], Attachment.prototype, "handleOrderChange", null);
__decorate([
    mobxAction
], Attachment.prototype, "changeOrder", null);
__decorate([
    autobind
], Attachment.prototype, "getSortSelectPopupContainer", null);
__decorate([
    autobind
], Attachment.prototype, "renderWrapper", null);
__decorate([
    autobind
], Attachment.prototype, "getTooltipValidationMessage", null);
__decorate([
    autobind
], Attachment.prototype, "handlePopupHiddenChange", null);
__decorate([
    mobxAction
], Attachment.prototype, "setPopup", null);
Attachment = __decorate([
    observer
], Attachment);
export default Attachment;
